/**
 * Chat API
 * Handles chat messages and SSE streaming
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BaseAPI } from "./base-api";
export class ChatAPI extends BaseAPI {
    /**
     * Send a message and handle streaming response via SSE
     */
    sendMessage(sessionId, message, onChunk, onComplete, onError) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const payload = {
                    query: message,
                    user_id: this.userId,
                };
                // Only include session_id if it exists
                if (sessionId) {
                    payload.session_id = sessionId;
                }
                const headers = {};
                // Add email header if provided
                if (this.config.emailAddress) {
                    headers["x-user-email"] = this.config.emailAddress;
                }
                console.log("[ChatAPI] Sending message to /query:", payload);
                const response = yield fetch(`${this.env.AGENT_BASE_URL}/query`, {
                    method: "POST",
                    headers: Object.assign({ "Content-Type": "application/json" }, headers),
                    body: JSON.stringify(payload),
                });
                console.log("[ChatAPI] Response status:", response.status);
                console.log("[ChatAPI] Response headers:", response.headers.get("content-type"));
                if (!response.ok) {
                    throw new Error(`Failed to send message: ${response.statusText}`);
                }
                // Check if response is SSE or regular JSON
                const contentType = response.headers.get("content-type");
                if (contentType === null || contentType === void 0 ? void 0 : contentType.includes("text/event-stream")) {
                    console.log("[ChatAPI] Handling as SSE stream");
                    yield this.handleSSEStream(response, onChunk, onComplete);
                }
                else {
                    console.log("[ChatAPI] Handling as regular JSON");
                    // Handle as regular JSON response
                    const data = yield response.json();
                    console.log("[ChatAPI] JSON response:", data);
                    if (data.response !== undefined) {
                        onChunk(data.response, data);
                    }
                    onComplete(data.session_id || "");
                }
            }
            catch (error) {
                console.error("Error sending message:", error);
                onError(error);
            }
        });
    }
    /**
     * Handle Server-Sent Events (SSE) stream
     */
    handleSSEStream(response, onChunk, onComplete) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const reader = (_a = response.body) === null || _a === void 0 ? void 0 : _a.getReader();
            const decoder = new TextDecoder();
            if (!reader) {
                throw new Error("Response body is not readable");
            }
            let buffer = "";
            let returnedSessionId = null;
            while (true) {
                const { done, value } = yield reader.read();
                if (done) {
                    // Pass session ID to onComplete
                    onComplete(returnedSessionId || "");
                    break;
                }
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop() || "";
                for (const line of lines) {
                    if (line.startsWith("data: ")) {
                        const data = line.slice(6).trim();
                        if (data && data !== "[DONE]") {
                            const sessionId = this.processSSEData(data, onChunk);
                            if (sessionId) {
                                returnedSessionId = sessionId;
                            }
                        }
                    }
                }
            }
        });
    }
    /**
     * Process individual SSE data chunks
     * Returns the session_id if found in the response
     */
    processSSEData(data, onChunk) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        try {
            const parsed = JSON.parse(data);
            console.log("[ChatAPI] Received SSE data:", parsed);
            // New format: { response, session_id, function_response }
            if (parsed.response !== undefined) {
                console.log("[ChatAPI] New format detected, response:", parsed.response);
                // Extract the text response (call onChunk even if response is empty string)
                onChunk(parsed.response || "", parsed);
                // Return session_id if present
                return parsed.session_id || null;
            }
            // Legacy format support (old API responses)
            if ((_c = (_b = (_a = parsed.content) === null || _a === void 0 ? void 0 : _a.parts) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.text) {
                const text = parsed.content.parts[0].text;
                onChunk(text, parsed);
            }
            else if ((_f = (_e = (_d = parsed.content) === null || _d === void 0 ? void 0 : _d.parts) === null || _e === void 0 ? void 0 : _e[0]) === null || _f === void 0 ? void 0 : _f.functionCall) {
                // Function call - pass along but no text chunk
                onChunk("", parsed);
            }
            else if ((_j = (_h = (_g = parsed.content) === null || _g === void 0 ? void 0 : _g.parts) === null || _h === void 0 ? void 0 : _h[0]) === null || _j === void 0 ? void 0 : _j.functionResponse) {
                // Function response - pass along but no text chunk
                onChunk("", parsed);
            }
            else if (parsed.chunk) {
                onChunk(parsed.chunk, parsed);
            }
            else if (parsed.text) {
                onChunk(parsed.text, parsed);
            }
            return null;
        }
        catch (e) {
            // If not JSON, treat as plain text chunk
            onChunk(data);
            return null;
        }
    }
}
